import { ISkeletonData, Spine } from "pixi-spine";
import { IDestroyOptions, Point, Sprite, Text, TextStyle, Texture, Ticker } from "pixi.js";

export class AnimatedRewardContainer extends Sprite {
    protected targetIconScale: number;
    protected text: Text;
    protected finalIcon: Sprite;
    protected iconGlow: Spine;
    protected cancelled: boolean = false;

    constructor(
        background: Texture,
        protected icon: Texture,
        text: string,
        textStyle: TextStyle,
        glowData: ISkeletonData,
        protected iconSize: number = 111) {
        super(background);
        this.targetIconScale = iconSize / icon.width;
        this.text = new Text(text, textStyle);
        this.addChild(this.text);
        this.scale.set(2);
        this.alpha = 0;
        this.pivot.set(background.width * 0.5, background.height * 0.5);
        this.iconGlow = new Spine(glowData);
    }

    public animateContainer(iconStart: Point, iconEnd: Point, iconsStartDelay: number, durationMS: number = 1000): Promise<void> {
        return new Promise((resolve) => {
            let elapsed = 0;
            let iconsStarted = false;
            let iconsDone = false;

            const tick = () => {
                if (this.destroyed || this.cancelled) {
                    Ticker.shared.remove(tick);
                    return resolve();
                }
                elapsed += Ticker.shared.elapsedMS;
                let t = Math.min(elapsed / durationMS, 1);

                const ease = 1 - Math.pow(1 - t, 3); // easeOutCubic
                const scaleValue = 2 - (2 - 1) * ease;

                this.scale.set(scaleValue);
                this.alpha = ease;

                this.text.scale.set(scaleValue);
                this.text.alpha = ease;

                if (!iconsStarted && t >= 0.5) {
                    iconsStarted = true;
                    this.animateIcons(iconStart, iconEnd, durationMS * 0.5, iconsStartDelay)
                        .then(() => {
                            iconsDone = true;
                            if (t >= 1 && !this.cancelled) {
                                Ticker.shared.remove(tick);
                                resolve();
                            }
                        });
                }

                if (t >= 1 && iconsDone) {
                    Ticker.shared.remove(tick);
                    resolve();
                }
            };

            Ticker.shared.add(tick);
        });
    }

    public glow(): void {
        if (this.cancelled) return;
        this.iconGlow.state.setAnimation(0, "coin_glow", false);
        this.iconGlow.position.set(this.finalIcon.position.x, this.finalIcon.position.y);
        this.addChild(this.iconGlow);

        this.iconGlow.state.addListener({
            complete: () => {
                requestAnimationFrame(() => {
                    if (this.cancelled || this.destroyed) {
                        if (this.iconGlow && this.iconGlow.parent) {
                            this.removeChild(this.iconGlow);
                        }
                        if (this.iconGlow) {
                            this.iconGlow.destroy();
                            this.iconGlow = null!;
                        }
                        return;
                    }

                    if (this.iconGlow && this.iconGlow.parent) {
                        this.removeChild(this.iconGlow);
                    }
                    if (this.iconGlow) {
                        this.iconGlow.destroy();
                        this.iconGlow = null!;
                    }
                });
            }
        });
    }

    private animateIcons(start: Point, end: Point, durationMS: number = 1000, delayMS: number = 250): Promise<void> {
        const icons: Sprite[] = [];
        const startScales = [0.4, 0.6, 0.8, 1.0, 1.2];

        for (let i = 0; i < 5; i++) {
            const iconClone = new Sprite(this.icon);
            iconClone.width = iconClone.height = this.iconSize;
            iconClone.scale.set(startScales[i]);
            iconClone.pivot.set(this.icon.width * 0.5, this.icon.height * 0.5);
            iconClone.x = start.x;
            iconClone.y = start.y;
            iconClone.alpha = 0;
            this.addChild(iconClone);
            icons.push(iconClone);
        }
        this.finalIcon = icons[icons.length - 1];

        const promises = icons.map((icon, i) => {
            // сохраняем стартовые скейлы
            const sxStart = icon.scale.x;
            const syStart = icon.scale.y;

            return new Promise<void>(async (resolve) => {
                await new Promise(r => setTimeout(r, i * delayMS));
                if (this.cancelled || this.destroyed) return resolve();

                let elapsed = 0;
                const tick = () => {
                    if (this.cancelled || this.destroyed) {
                        Ticker.shared.remove(tick);
                        return resolve();
                    }
                    elapsed += Ticker.shared.elapsedMS;
                    let t = Math.min(elapsed / durationMS, 1);
                    const ease = 1 - Math.pow(1 - t, 3);

                    // движение по Y
                    icon.y = start.y + (end.y - start.y) * ease;

                    // масштабирование к targetScale (из фиксированных стартовых значений)
                    icon.scale.set(
                        sxStart + (this.targetIconScale - sxStart) * ease,
                        syStart + (this.targetIconScale - syStart) * ease
                    );

                    icon.alpha = ease;

                    if (t >= 1) {
                        Ticker.shared.remove(tick);
                        resolve();
                    }
                };

                Ticker.shared.add(tick);
            });
        });

        return Promise.all(promises).then(() => {
            if (!this.cancelled && !this.destroyed) {
                for (let i = 0; i < icons.length - 1; i++) {
                    this.removeChild(icons[i]);
                }
            }
        });

    }

    public cancelAnimations(): void {
        this.cancelled = true;
        if (this.iconGlow && this.iconGlow.parent) {
            this.removeChild(this.iconGlow);
            this.iconGlow.destroy();
            this.iconGlow = null!;
        }
    }

    public override destroy(options?: IDestroyOptions | boolean): void {
        this.cancelAnimations();
        if (this.iconGlow) {
            this.iconGlow.state.clearListeners();
            if (this.iconGlow.parent) {
                this.removeChild(this.iconGlow);
            }
            this.iconGlow.destroy(options);
            this.iconGlow = null!;
        }
        super.destroy(options);
    }
}